import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { isNativeApp } from '../lib/native';
import { useSubscription } from '../contexts/SubscriptionContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, LogOut, AlertTriangle, Mail, CreditCard, Check, Loader2 } from 'lucide-react';
import { Link } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COPY = {
  suspended: {
    title: 'Account suspended',
    headline: 'Your subscription is currently suspended.',
    body: 'Access to CivilX TMS has been temporarily disabled for non-payment. Update your billing to restore full access immediately.',
    badge: 'SUSPENDED',
    badgeColor: 'bg-red-100 text-red-700',
  },
  cancelled: {
    title: 'Account cancelled',
    headline: 'Your subscription has been cancelled.',
    body: 'Your data is preserved. Re-subscribe at any time to regain full access — or contact your administrator.',
    badge: 'CANCELLED',
    badgeColor: 'bg-slate-100 text-slate-700',
  },
};

const SubscriptionSuspended = () => {
  const { user, logout } = useAuth();
  const { subscription } = useSubscription();
  const status = subscription?.status || 'suspended';
  const copy = STATUS_COPY[status] || STATUS_COPY.suspended;

  const [plans, setPlans] = useState([]);
  const [busyPlan, setBusyPlan] = useState(null);
  const isAdmin = user?.role === 'company_admin';

  useEffect(() => {
    if (!isAdmin) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/pricing/plans`, { withCredentials: true });
        setPlans(Array.isArray(res.data) ? res.data : []);
      } catch {
        // best-effort — page still works, user can click Update billing
      }
    })();
  }, [isAdmin]);

  const subscribe = async (planId) => {
    setBusyPlan(planId);
    try {
      const { data } = await axios.post(
        `${API}/checkout/session`,
        { plan_id: planId, origin_url: window.location.origin },
        { withCredentials: true },
      );
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Could not start checkout — please try Update billing.');
        setBusyPlan(null);
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not start checkout');
      setBusyPlan(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4" data-testid="subscription-suspended-page">
      <div className="max-w-3xl mx-auto bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-red-50 border-b border-red-100 px-8 py-6 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <Lock className="w-6 h-6 text-red-600" strokeWidth={2.25} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-red-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {copy.badge}
            </p>
            <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
              {copy.title}
            </h1>
          </div>
        </div>

        <div className="px-8 py-7">
          <p className="text-base text-slate-900 font-semibold mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>
            {copy.headline}
          </p>
          <p className="text-sm text-slate-600 mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {copy.body} <strong>Your data is preserved</strong> — subscribe below to instantly restore full access.
          </p>

          {subscription?.override_note && (
            <div className="mb-6 p-3 bg-slate-50 border border-slate-200 rounded-md">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Reason
              </p>
              <p className="text-sm text-slate-700 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {subscription.override_note}
              </p>
            </div>
          )}

          {/* Inline plan picker for company admins (never inside the App Store shell — guideline 3.1.1) */}
          {isAdmin && isNativeApp() && (
            <p className="mb-6 text-sm text-slate-600" data-testid="suspended-native-note" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Access to this company workspace is currently paused. Please contact your company administrator to restore access.
            </p>
          )}
          {isAdmin && !isNativeApp() && plans.length > 0 && (
            <div className="mb-6" data-testid="suspended-plans-grid">
              <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                Pick a plan to restore access
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {plans.map((plan) => (
                  <div
                    key={plan.id}
                    data-testid={`suspended-plan-${plan.id}`}
                    className="border border-slate-200 rounded-md p-4 flex flex-col"
                  >
                    <p className="text-sm font-bold text-slate-900 mb-1" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                      {plan.name}
                    </p>
                    <p className="text-xs text-slate-600 mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <span className="text-2xl font-bold text-slate-900">${Number(plan.price).toFixed(0)}</span>
                      <span className="text-slate-500"> /month</span>
                    </p>
                    <ul className="text-[11px] text-slate-600 space-y-1 mb-4 flex-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      {(plan.features || []).slice(0, 5).map((f) => (
                        <li key={f} className="flex items-start gap-1">
                          <Check className="w-3 h-3 text-emerald-600 flex-shrink-0 mt-0.5" /> {f}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={() => subscribe(plan.id)}
                      disabled={busyPlan === plan.id}
                      data-testid={`suspended-subscribe-${plan.id}`}
                      className="w-full inline-flex items-center justify-center gap-1 px-3 py-2 bg-[#002FA7] hover:bg-[#00247D] text-white rounded text-xs font-bold uppercase tracking-widest disabled:opacity-50"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    >
                      {busyPlan === plan.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CreditCard className="w-3 h-3" />}
                      {busyPlan === plan.id ? 'Redirecting…' : 'Subscribe'}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-3 mb-6">
            <div className="flex items-start gap-3 text-sm text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
              <span>While your account is {status}, you cannot create, edit or view fleet data. Your data stays safe in our system.</span>
            </div>
            <div className="flex items-start gap-3 text-sm text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <Mail className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <span>Need help? Contact your company administrator or CivilX TMS support.</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            {isAdmin && !isNativeApp() && (
              <Link
                to="/billing"
                data-testid="suspended-go-billing"
                className="flex-1 bg-white border border-[#002FA7] text-[#002FA7] px-5 py-3 rounded-md font-semibold text-sm hover:bg-blue-50 transition-all flex items-center justify-center gap-2"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                <CreditCard className="w-4 h-4" />
                Manage billing
              </Link>
            )}
            <button
              onClick={logout}
              data-testid="suspended-logout"
              className="flex-1 sm:flex-none border border-slate-300 text-slate-700 px-5 py-3 rounded-md font-semibold text-sm hover:bg-slate-50 transition-all flex items-center justify-center gap-2"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>
        </div>

        <div className="border-t border-slate-100 bg-slate-50 px-8 py-3 text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
          Signed in as <span className="font-semibold text-slate-700">{user?.email}</span>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionSuspended;
